<main class="marketplace-page">
  <header class="marketplace-heading">
    <div>
      @if (filters.category) {
        <span class="breadcrumb">{{ t.getMarketplaceBrowseText() }}</span>
      }
      <h1>
        {{
          filters.category
            ? categoryName(filters.category)
            : t.getMarketplaceBrowseText()
        }}
      </h1>
    </div>
    <div class="heading-counts">
      <span class="result-count">{{
        t.getMarketplaceProductsCountText(totalRecords)
      }}</span>
      @if (marketplace.cartCount()) {
        <span class="selected-count" role="status">{{
          t.getMarketplaceSelectionCountText(marketplace.cartCount())
        }}</span>
      }
    </div>
  </header>

  <div
    class="category-tabs"
    role="group"
    [attr.aria-label]="t.getMarketplaceCategoryLabelText()"
  >
    <button
      type="button"
      [class.active]="!filters.category"
      [attr.aria-pressed]="!filters.category"
      (click)="categoryChanged('')"
    >
      {{ t.getMarketplaceAllProductsText() }}
    </button>
    @for (category of facets.categories; track category) {
      <button
        type="button"
        [class.active]="filters.category === category"
        [attr.aria-pressed]="filters.category === category"
        (click)="categoryChanged(category)"
      >
        {{ categoryName(category) }}
      </button>
    }
  </div>
  @if (filters.category) {
    <div
      class="subcategory-tabs"
      role="group"
      [attr.aria-label]="t.getMarketplaceSubcategoryLabelText()"
    >
      <button
        type="button"
        [class.active]="!filters.subcategory"
        [attr.aria-pressed]="!filters.subcategory"
        (click)="subcategoryChanged('')"
      >
        {{ t.getMarketplaceAllInCategoryText() }}
      </button>
      @for (subcategory of facets.subcategories; track subcategory) {
        <button
          type="button"
          [class.active]="filters.subcategory === subcategory"
          [attr.aria-pressed]="filters.subcategory === subcategory"
          (click)="subcategoryChanged(subcategory)"
        >
          {{ subcategoryName(subcategory) }}
        </button>
      }
    </div>
  }

  <div class="marketplace-controls">
    <mat-form-field
      appearance="outline"
      class="search-field"
      subscriptSizing="dynamic"
    >
      <mat-icon matPrefix>search</mat-icon>
      <input
        matInput
        [(ngModel)]="filters.search"
        (ngModelChange)="searchChanged()"
        [placeholder]="t.getMarketplaceSearchText()"
        [attr.aria-label]="t.getMarketplaceSearchText()"
      />
    </mat-form-field>
    <mat-form-field
      appearance="outline"
      subscriptSizing="dynamic"
      class="sort-field"
    >
      <mat-label>{{ t.getMarketplaceSortText() }}</mat-label>
      <mat-select [(ngModel)]="filters.sort" (selectionChange)="sortChanged()">
        <mat-option value="newest">{{
          t.getMarketplaceNewestText()
        }}</mat-option>
        <mat-option value="nameAsc">{{
          t.getMarketplaceNameAscText()
        }}</mat-option>
        <mat-option value="nameDesc">{{
          t.getMarketplaceNameDescText()
        }}</mat-option>
      </mat-select>
    </mat-form-field>
    <button
      #filterButton
      mat-stroked-button
      type="button"
      class="mobile-filter-button"
      [attr.aria-expanded]="filterPanelOpen"
      aria-controls="marketplace-filters"
      (click)="openFilters()"
    >
      <mat-icon>tune</mat-icon>{{ t.getMarketplaceFiltersText() }}
      @if (activeFilterCount) {
        <span class="filter-count">{{ activeFilterCount }}</span>
      }
    </button>
    <div
      class="view-switch"
      role="group"
      [attr.aria-label]="t.getMarketplaceBrowseText()"
    >
      <button
        mat-icon-button
        type="button"
        [attr.aria-label]="t.getMarketplaceGridText()"
        [attr.aria-pressed]="view === 'grid'"
        [class.active]="view === 'grid'"
        (click)="view = 'grid'"
      >
        <mat-icon>grid_view</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        [attr.aria-label]="t.getMarketplaceListText()"
        [attr.aria-pressed]="view === 'list'"
        [class.active]="view === 'list'"
        (click)="view = 'list'"
      >
        <mat-icon>view_list</mat-icon>
      </button>
    </div>
  </div>

  <div class="marketplace-layout">
    <aside
      class="filter-sidebar"
      id="marketplace-filters"
      [cdkTrapFocus]="filterPanelOpen"
      [cdkTrapFocusAutoCapture]="filterPanelOpen"
      [attr.role]="filterPanelOpen ? 'dialog' : 'region'"
      [attr.aria-modal]="filterPanelOpen ? true : null"
      tabindex="-1"
      (keydown.escape)="closeFilters()"
      [class.open]="filterPanelOpen"
      [attr.aria-label]="t.getMarketplaceFiltersText()"
    >
      <div class="filter-header">
        <h2>{{ t.getMarketplaceFiltersText() }}</h2>
        <button type="button" class="reset-button" (click)="resetFilters()">
          {{ t.getResetText() }}
        </button>
        <button
          mat-icon-button
          type="button"
          class="close-filters"
          [attr.aria-label]="t.getBackText()"
          (click)="closeFilters()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="filter-scroll">
        <section class="filter-section attribute-filters">
          <h3>{{ t.getMarketplaceAttributesText() }}</h3>
          @if (customFieldLimitReached) {
            <p role="status">{{ t.getMarketplaceAttributeLimitText() }}</p>
          } @else {
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ t.getMarketplaceAddAttributeText() }}</mat-label>
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [formControl]="customFieldSearch"
                [matAutocomplete]="customFieldAutocomplete"
                (focus)="searchCustomFields(customFieldSearch.value, true)"
              />
              <mat-autocomplete
                #customFieldAutocomplete="matAutocomplete"
                [displayWith]="customFieldDisplay"
                (optionSelected)="addCustomField($event.option.value)"
              >
                @for (field of customFieldSuggestions; track field.key) {
                  <mat-option [value]="field">
                    <span class="attribute-option-name">{{ field.name }}</span>
                    <span class="attribute-option-kind">
                      {{ customTypeLabel(field) }}
                      @if (field.unit) {
                        · {{ field.unit }}
                      }
                    </span>
                  </mat-option>
                }
                @if (customFieldSearchLoading) {
                  <mat-option disabled>{{
                    t.getMarketplaceLoadingAttributesText()
                  }}</mat-option>
                } @else if (customFieldSearchFailed) {
                  <mat-option disabled>{{
                    t.getMarketplaceAttributeLoadFailedText()
                  }}</mat-option>
                } @else if (
                  !customFieldSuggestions.length && !customFieldSearchHasMore
                ) {
                  <mat-option disabled>
                    @if (customFieldSearchHasResults) {
                      {{ t.getMarketplaceAttributesAlreadyAddedText() }}
                    } @else if (customFieldSearchEmpty) {
                      {{ t.getMarketplaceNoAvailableAttributesText() }}
                    } @else if (customFieldSearchNeedsMore) {
                      {{ t.getMarketplaceRefineAttributeSearchText() }}
                    } @else {
                      {{ t.getMarketplaceNoAttributesText() }}
                    }
                  </mat-option>
                }
                @if (customFieldSearchHasMore) {
                  <mat-option disabled>{{
                    t.getMarketplaceRefineAttributeSearchText()
                  }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          }
        </section>
        @for (field of selectedCustomFields; track field.key) {
          <section
            class="filter-section custom-field-filter"
            role="group"
            [attr.aria-label]="customFacetLabel(field)"
          >
            <div class="custom-filter-heading">
              <h3>
                {{ field.name }}
                @if (field.unit) {
                  ({{ field.unit }})
                }
              </h3>
              <button
                mat-icon-button
                type="button"
                [attr.aria-label]="
                  t.getRemoveText() + ' ' + customFacetLabel(field)
                "
                (click)="removeCustomField(field)"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
            @if (field.type === 'number') {
              <div class="range-fields">
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t.getMarketplaceMinText() }}</mat-label>
                  <input
                    matInput
                    type="number"
                    step="any"
                    [placeholder]="field.min?.toString() ?? ''"
                    #customMin
                    [attr.aria-label]="
                      t.getMarketplaceMinText() + ' ' + customFacetLabel(field)
                    "
                    [attr.aria-invalid]="
                      invalidCustomRangeInputs.has(customMin)
                    "
                    [ngModel]="customFilter(field)?.min ?? null"
                    (input)="customRangeChanged(field, 'min', customMin)"
                  />
                </mat-form-field>
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t.getMarketplaceMaxText() }}</mat-label>
                  <input
                    matInput
                    type="number"
                    step="any"
                    [placeholder]="field.max?.toString() ?? ''"
                    #customMax
                    [attr.aria-label]="
                      t.getMarketplaceMaxText() + ' ' + customFacetLabel(field)
                    "
                    [attr.aria-invalid]="
                      invalidCustomRangeInputs.has(customMax)
                    "
                    [ngModel]="customFilter(field)?.max ?? null"
                    (input)="customRangeChanged(field, 'max', customMax)"
                  />
                </mat-form-field>
              </div>
            } @else if (field.type === 'boolean') {
              @for (value of field.values; track value) {
                <mat-checkbox
                  [checked]="
                    customFilter(field)?.values?.includes(value) ?? false
                  "
                  [aria-label]="
                    customValueLabel(value) + ', ' + customFacetLabel(field)
                  "
                  (change)="toggleCustomChoice(field, value)"
                  >{{ customValueLabel(value) }}</mat-checkbox
                >
              }
            } @else {
              @if (customFilter(field)?.values?.length) {
                <div class="selected-facets">
                  @for (value of customFilter(field)?.values; track value) {
                    <button
                      type="button"
                      class="facet-chip"
                      [attr.aria-label]="
                        t.getRemoveText() +
                        ' ' +
                        customValueLabel(value) +
                        ', ' +
                        field.name
                      "
                      (click)="toggleCustomChoice(field, value)"
                    >
                      <span>{{ customValueLabel(value) }}</span>
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  }
                </div>
              }
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ t.getMarketplaceSearchValuesText() }}</mat-label>
                <mat-icon matPrefix>search</mat-icon>
                <input
                  matInput
                  [ngModel]="field.search"
                  (ngModelChange)="searchCustomValues(field, $event)"
                  [matAutocomplete]="customValueAutocomplete"
                  [attr.aria-label]="
                    t.getMarketplaceSearchValuesText() + ' ' + field.name
                  "
                />
                <mat-autocomplete
                  #customValueAutocomplete="matAutocomplete"
                  (optionSelected)="
                    selectCustomValue(field, $event.option.value)
                  "
                >
                  @for (value of matchingCustomValues(field); track value) {
                    <mat-option [value]="value">{{ value }}</mat-option>
                  }
                  @if (field.loading) {
                    <mat-option disabled>{{
                      t.getMarketplaceLoadingValuesText()
                    }}</mat-option>
                  } @else if (
                    !field.failed && !matchingCustomValues(field).length
                  ) {
                    <mat-option disabled>{{ t.getNoResultsText() }}</mat-option>
                  }
                  @if (field.hasMore) {
                    <mat-option disabled>{{
                      t.getMarketplaceRefineValueSearchText()
                    }}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            }
            @if (field.failed) {
              <p class="attribute-load-error" role="alert">
                {{ t.getMarketplaceValueLoadFailedText() }}
                <button
                  type="button"
                  class="reset-button"
                  (click)="searchCustomValues(field, field.search)"
                >
                  {{ t.getMarketplaceRetryText() }}
                </button>
              </p>
            }
          </section>
        }
        <section class="filter-section">
          <h3>{{ t.getMarketplacePriceRangeText() }}</h3>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t.getMarketplaceCurrencyText() }}</mat-label>
            <mat-select
              [(ngModel)]="draft.currency"
              (selectionChange)="filterChanged()"
            >
              <mat-select-trigger>{{
                draft.currency || t.getMarketplaceAllCurrenciesText()
              }}</mat-select-trigger>
              <mat-option value="">{{
                t.getMarketplaceAllCurrenciesText()
              }}</mat-option>
              @for (currency of currencies; track currency.code) {
                <mat-option [value]="currency.code"
                  >{{ currency.code }} · {{ currency.name }}</mat-option
                >
              }
            </mat-select>
          </mat-form-field>
          <div class="range-fields">
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getMarketplaceMinText() }}</mat-label
              ><input
                matInput
                type="number"
                min="0"
                [(ngModel)]="draft.minPrice"
                (ngModelChange)="filterChanged()"
            /></mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getMarketplaceMaxText() }}</mat-label
              ><input
                matInput
                type="number"
                min="0"
                [(ngModel)]="draft.maxPrice"
                (ngModelChange)="filterChanged()"
            /></mat-form-field>
          </div>
          <mat-checkbox
            [(ngModel)]="draft.includeUnpriced"
            (change)="filterChanged()"
            >{{ t.getMarketplaceIncludeUnpricedText() }}</mat-checkbox
          >
        </section>
        <section class="filter-section">
          <h3>{{ t.getMarketplaceBrandText() }}</h3>
          @if (draft.brand.length) {
            <div class="selected-facets">
              @for (brand of draft.brand; track brand) {
                <button
                  type="button"
                  class="facet-chip"
                  [attr.aria-label]="t.getRemoveText() + ' ' + brand"
                  (click)="toggleChoice('brand', brand)"
                >
                  <span>{{ brand }}</span>
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              }
            </div>
          }
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t.getSearchText() }}</mat-label>
            <mat-icon matPrefix>search</mat-icon>
            <input
              matInput
              [formControl]="brandSearch"
              [matAutocomplete]="brandAutocomplete"
              [attr.aria-label]="
                t.getSearchText() + ' ' + t.getMarketplaceBrandText()
              "
            />
            <mat-autocomplete
              #brandAutocomplete="matAutocomplete"
              (optionSelected)="selectBrand($event.option.value)"
            >
              @for (brand of matchingBrands; track brand) {
                <mat-option [value]="brand">{{ brand }}</mat-option>
              }
              @if (brandSearch.value.trim() && !matchingBrands.length) {
                <mat-option disabled>{{ t.getNoResultsText() }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </section>
        <section class="filter-section">
          <h3>{{ t.getMarketplaceSupplierText() }}</h3>
          @if (draft.supplier.length) {
            <div class="selected-facets">
              @for (id of draft.supplier; track id) {
                <button
                  type="button"
                  class="facet-chip"
                  [attr.aria-label]="t.getRemoveText() + ' ' + sellerName(id)"
                  (click)="toggleChoice('supplier', id)"
                >
                  <span>{{ sellerName(id) }}</span>
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              }
            </div>
          }
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t.getSearchText() }}</mat-label>
            <mat-icon matPrefix>search</mat-icon>
            <input
              matInput
              [formControl]="sellerSearch"
              [matAutocomplete]="sellerAutocomplete"
              [attr.aria-label]="
                t.getSearchText() + ' ' + t.getMarketplaceSupplierText()
              "
            />
            <mat-autocomplete
              #sellerAutocomplete="matAutocomplete"
              (optionSelected)="selectSeller($event.option.value)"
            >
              @for (seller of matchingSellers; track seller.id) {
                <mat-option [value]="seller.id">{{ seller.name }}</mat-option>
              }
              @if (sellerSearch.value.trim() && !matchingSellers.length) {
                <mat-option disabled>{{ t.getNoResultsText() }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </section>
        <section class="filter-section">
          <h3>{{ t.getMarketplaceWeightRangeText() }}</h3>
          <mat-form-field appearance="outline" subscriptSizing="dynamic"
            ><mat-label>{{ t.getMarketplaceWeightUnitText() }}</mat-label
            ><mat-select
              [(ngModel)]="draft.weightUnit"
              (selectionChange)="filterChanged()"
            >
              @for (unit of facets.weightUnits; track unit) {
                <mat-option [value]="unit">{{ unit }}</mat-option>
              }
            </mat-select></mat-form-field
          >
          <div class="range-fields">
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getMarketplaceMinText() }}</mat-label
              ><input
                matInput
                type="number"
                min="0"
                [(ngModel)]="draft.minWeight"
                (ngModelChange)="filterChanged()" /></mat-form-field
            ><mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getMarketplaceMaxText() }}</mat-label
              ><input
                matInput
                type="number"
                min="0"
                [(ngModel)]="draft.maxWeight"
                (ngModelChange)="filterChanged()"
            /></mat-form-field>
          </div>
        </section>
        <section class="filter-section">
          <h3>{{ t.getMarketplaceConditionText() }}</h3>
          @for (condition of productConditions; track condition) {
            <mat-checkbox
              [checked]="hasChoice('condition', condition)"
              (change)="toggleChoice('condition', condition)"
              >{{ condition }}</mat-checkbox
            >
          }
        </section>
        @if (filters.category === 'Drones') {
          <section class="filter-section">
            <h3>{{ t.getMarketplaceFlightRangeText() }}</h3>
            @for (range of ['under30', '30to44', '45plus']; track range) {
              <mat-checkbox
                [checked]="hasChoice('flightRange', range)"
                (change)="toggleChoice('flightRange', range)"
                >{{ flightLabel(range) }}</mat-checkbox
              >
            }
          </section>
          <section class="filter-section">
            <h3>{{ t.getMarketplaceDroneTypeText() }}</h3>
            @for (type of facets.droneTypes; track type) {
              <mat-checkbox
                [checked]="hasChoice('droneType', type)"
                (change)="toggleChoice('droneType', type)"
                >{{ type }}</mat-checkbox
              >
            }
          </section>
          <section class="filter-section">
            <h3>{{ t.getMarketplaceVideoResolutionText() }}</h3>
            @for (value of facets.videoResolutions; track value) {
              <mat-checkbox
                [checked]="hasChoice('videoResolution', value)"
                (change)="toggleChoice('videoResolution', value)"
                >{{ value }}</mat-checkbox
              >
            }
          </section>
          <section class="filter-section">
            <h3>{{ t.getMarketplaceSensorSizeText() }}</h3>
            @for (value of facets.sensorSizes; track value) {
              <mat-checkbox
                [checked]="hasChoice('sensorSize', value)"
                (change)="toggleChoice('sensorSize', value)"
                >{{ value }}</mat-checkbox
              >
            }
          </section>
          <section class="filter-section">
            <h3>{{ t.getMarketplaceFpvText() }}</h3>
            <mat-checkbox
              [checked]="hasChoice('fpv', 'true')"
              (change)="toggleChoice('fpv', 'true')"
              >{{ t.getYesText() }}</mat-checkbox
            ><mat-checkbox
              [checked]="hasChoice('fpv', 'false')"
              (change)="toggleChoice('fpv', 'false')"
              >{{ t.getNoText() }}</mat-checkbox
            >
          </section>
        }
      </div>
      @if (invalidRange) {
        <p class="validation-error" role="alert">
          {{
            invalidCustomRangeInputs.size > 0
              ? t.getMarketplaceInvalidNumberText()
              : t.getMarketplaceInvalidRangeText()
          }}
        </p>
      }
      @if (weightUnitMissing) {
        <p class="validation-error" role="alert">
          {{ t.getMarketplaceWeightUnitRequiredText() }}
        </p>
      }
      @if (customFieldFilterLimitExceeded) {
        <p class="validation-error" role="alert">
          {{ t.getMarketplaceAttributeSelectionLimitText() }}
        </p>
      }
      <button
        mat-flat-button
        type="button"
        class="apply-button"
        [disabled]="
          invalidRange || weightUnitMissing || customFieldFilterLimitExceeded
        "
        (click)="applyFilters()"
      >
        {{ t.getMarketplaceApplyText() }}
      </button>
    </aside>

    <section class="results" [attr.aria-label]="t.getMarketplaceBrowseText()">
      @if (activeCustomFilters.length) {
        <div
          class="applied-attributes"
          role="group"
          [attr.aria-label]="t.getMarketplaceAppliedAttributesText()"
        >
          @for (field of activeCustomFilters; track $index) {
            <button
              type="button"
              class="facet-chip"
              [attr.aria-label]="
                t.getRemoveText() + ' ' + customConditionLabel(field)
              "
              (click)="removeAppliedCustomFilter(field)"
            >
              <span>{{ customConditionLabel(field) }}</span>
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          }
        </div>
      }
      @if (loading) {
        <div class="state" role="status">
          <mat-spinner diameter="36"></mat-spinner
          >{{ t.getMarketplaceLoadingText() }}
        </div>
      } @else if (failed) {
        <div class="state" role="alert">
          {{ t.getMarketplaceLoadFailedText()
          }}<button mat-stroked-button type="button" (click)="load()">
            {{ t.getMarketplaceRetryText() }}
          </button>
        </div>
      } @else if (products.length === 0) {
        <div class="state">{{ t.getMarketplaceNoResultsText() }}</div>
      } @else {
        <div class="product-results" [class.list-view]="view === 'list'">
          @for (product of products; track product.id) {
            <article class="product-card">
              <a
                class="product-image"
                [routerLink]="['/marketplace', product.id]"
                [attr.aria-label]="
                  t.getMarketplaceViewDetailsText() + ': ' + product.name
                "
              >
                <app-product-image
                  [picture]="product.picture"
                  [name]="product.name"
                />
              </a>
              <div class="product-info">
                <span class="brand">{{ product.brand }}</span>
                <h3>
                  <a [routerLink]="['/marketplace', product.id]">{{
                    product.name
                  }}</a>
                </h3>
                <p class="seller">
                  {{ t.getMarketplaceSupplierText() }}: {{ product.seller }}
                </p>
                @if (product.isOwnProduct) {
                  <span class="own-listing">{{
                    t.getMarketplaceOwnListingText()
                  }}</span>
                }
                @if (product.marketplaceSubcategory) {
                  <p class="subcategory">
                    {{ subcategoryName(product.marketplaceSubcategory) }}
                  </p>
                }
                <div class="specs">
                  @if (product.measureValue !== null) {
                    <span
                      >{{ product.measureValue }}
                      {{ product.measureUnit }}</span
                    >
                  }
                  @if (product.droneFlightTimeMinutes !== null) {
                    <span
                      >{{ product.droneFlightTimeMinutes }}
                      {{ t.getMarketplaceMinutesText() }}</span
                    >
                  }
                  @if (product.droneVideoResolution) {
                    <span>{{ product.droneVideoResolution }}</span>
                  }
                </div>
                <strong class="price">{{ price(product) }}</strong>
                @if (product.allowRequests && marketplace.canUseCart()) {
                  @if (!marketplace.isSelected(product.id)) {
                    <div class="add-quantity">
                      <label [for]="'marketplace-quantity-' + product.id">{{
                        t.getQuantityText()
                      }}</label>
                      <div class="quantity-field">
                        <input
                          [id]="'marketplace-quantity-' + product.id"
                          type="number"
                          [attr.inputmode]="
                            marketplace.wholeUnit(product.requestUnit)
                              ? 'numeric'
                              : 'decimal'
                          "
                          [attr.min]="
                            marketplace.wholeUnit(product.requestUnit)
                              ? '1'
                              : '0'
                          "
                          [attr.step]="
                            marketplace.wholeUnit(product.requestUnit)
                              ? '1'
                              : 'any'
                          "
                          [value]="quantityDrafts[product.id] ?? '1'"
                          [disabled]="
                            marketplace.pendingId() !== null ||
                            marketplace.cartLoading()
                          "
                          [attr.aria-invalid]="
                            invalidQuantityId === product.id ? 'true' : null
                          "
                          [attr.aria-describedby]="
                            invalidQuantityId === product.id
                              ? 'marketplace-quantity-error-' + product.id
                              : null
                          "
                          (input)="quantityChanged(product.id, $event)"
                        />
                        <span>{{
                          t.getMarketplaceRequestUnitValueText(
                            product.requestUnit
                          )
                        }}</span>
                      </div>
                      @if (invalidQuantityId === product.id) {
                        <p
                          class="quantity-error"
                          role="alert"
                          [id]="'marketplace-quantity-error-' + product.id"
                        >
                          {{
                            marketplace.wholeUnit(product.requestUnit)
                              ? t.getRequisitionCartWholeQuantityText()
                              : t.getRequisitionCartPositiveQuantityText()
                          }}
                        </p>
                      }
                    </div>
                  }
                  <button
                    mat-flat-button
                    type="button"
                    class="select-button"
                    (click)="select(product)"
                    [disabled]="
                      marketplace.pendingId() !== null ||
                      marketplace.cartLoading()
                    "
                  >
                    {{
                      marketplace.isSelected(product.id)
                        ? t.getMarketplaceRemoveSelectionText()
                        : t.getMarketplaceSelectText()
                    }}
                  </button>
                } @else if (!product.allowRequests && !product.isOwnProduct) {
                  <p class="unavailable">
                    <mat-icon aria-hidden="true">info_outline</mat-icon>
                    <span>{{ t.getMarketplaceRequestsUnavailableText() }}</span>
                  </p>
                }
                <a
                  class="details-link"
                  [routerLink]="['/marketplace', product.id]"
                  >{{ t.getMarketplaceViewDetailsText() }}
                  <mat-icon>arrow_forward</mat-icon></a
                >
              </div>
            </article>
          }
        </div>
        <mat-paginator
          [length]="totalRecords"
          [pageIndex]="filters.page - 1"
          [pageSize]="filters.pageSize"
          [hidePageSize]="true"
          (page)="pageChanged($event)"
          [attr.aria-label]="t.getMarketplaceBrowseText()"
        ></mat-paginator>
      }
    </section>
  </div>
  @if (marketplace.cartError()) {
    <p class="selection-note" role="alert">
      {{ t.getRequisitionCartErrorText(marketplace.cartError()) }}
    </p>
  }
  @if (marketplace.cartLoadFailed()) {
    <p class="selection-note" role="alert">
      {{ t.getRequisitionCartLoadFailedText() }}
    </p>
  }
  @if (marketplace.cartCount()) {
    <p class="selection-note" role="status">
      {{ t.getMarketplaceSelectionCountText(marketplace.cartCount()) }}.
      <a routerLink="/requisition-cart">{{ t.getRequisitionCartText() }}</a>
    </p>
  }
</main>
